import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Navigate } from 'react-router-dom';
import Icon from '../Shared/Icons';
import { useAuth } from '../../context/AuthContext';
import { getProgram, CLUB_TYPES } from '../../config/programs';
import { fetchClubs, saveClub, deleteClub, fetchMembers, resolveAuthorName } from '../../utils/programData';
import { ProgramShell, S, T } from './ui';

const DAYS = ['Jumatatu', 'Jumanne', 'Jumatano', 'Alhamisi', 'Ijumaa', 'Jumamosi', 'Jumapili'];

const EMPTY = {
  name: '', type: '', location: '', region: 'Arusha', district: '',
  meetingDay: '', meetingTime: '', coachNames: '', notes: '',
};

export default function Clubs() {
  const { pid } = useParams();
  const navigate = useNavigate();
  const { currentUser, userRole } = useAuth();
  const program = getProgram(pid);

  const [clubs, setClubs] = useState([]);
  const [counts, setCounts] = useState({});
  const [form, setForm] = useState({ ...EMPTY, type: program?.clubTypes[0] || 'shule' });
  const [editingId, setEditingId] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState('');
  const [err, setErr] = useState('');

  useEffect(() => { if (program) load(); /* eslint-disable-next-line */ }, [pid]);

  async function load() {
    try {
      const [cs, ms] = await Promise.all([fetchClubs(pid), fetchMembers(pid)]);
      setClubs(cs);
      const c = {};
      ms.forEach(m => { if (m.clubId) c[m.clubId] = (c[m.clubId] || 0) + 1; });
      setCounts(c);
    } catch (e) { console.error(e); }
  }

  if (!program) return <Navigate to="/programs" />;

  function change(k, v) { setForm(f => ({ ...f, [k]: v })); }

  function reset() {
    setForm({ ...EMPTY, type: program.clubTypes[0] });
    setEditingId(null); setShowForm(false);
  }

  async function submit() {
    if (!form.name.trim()) { setErr('Andika jina la club.'); return; }
    setSaving(true); setErr('');
    try {
      const name = await resolveAuthorName(currentUser);
      await saveClub({ ...form, programId: pid, createdBy: currentUser.uid, createdByName: name }, editingId);
      setMsg(editingId ? 'Club imebadilishwa.' : 'Club imetengenezwa.');
      reset(); load();
      setTimeout(() => setMsg(''), 3000);
    } catch (e) { console.error(e); setErr('Imeshindikana kuhifadhi.'); }
    setSaving(false);
  }

  async function remove(id) {
    if (counts[id]) {
      setErr('Huwezi kufuta club yenye wanachama. Wahamishe kwanza.');
      return;
    }
    if (!window.confirm('Futa club hii?')) return;
    try { await deleteClub(id); load(); } catch (e) { console.error(e); }
  }

  function edit(c) {
    setForm({
      name: c.name || '', type: c.type || program.clubTypes[0], location: c.location || '',
      region: c.region || 'Arusha', district: c.district || '',
      meetingDay: c.meetingDay || '', meetingTime: c.meetingTime || '',
      coachNames: c.coachNames || '', notes: c.notes || '',
    });
    setEditingId(c.id); setShowForm(true); window.scrollTo(0, 0);
  }

  return (
    <ProgramShell program={program} tab="clubs">

      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '14px', gap: '10px' }}>
        <div>
          <h2 style={{ ...S.h2, marginBottom: '3px' }}>Vilabu vya Chess</h2>
          <div style={{ fontSize: '12px', color: T.muted }}>
            Anza na za shule, ongeza za mtaani wakati wowote
          </div>
        </div>
        <button style={{ ...S.btn, background: program.color, padding: '11px 18px' }}
          onClick={() => (showForm ? reset() : setShowForm(true))}>
          {showForm ? 'Funga' : '+ Club'}
        </button>
      </div>

      {msg && <div style={S.ok}>{msg}</div>}
      {err && <div style={S.err}>{err}</div>}

      {showForm && (
        <div style={S.card}>
          <h2 style={S.h2}>{editingId ? 'Badilisha club' : 'Club mpya'}</h2>

          <label style={S.label}>Aina ya club</label>
          <div style={{ display: 'flex', gap: '8px', marginBottom: '18px', flexWrap: 'wrap' }}>
            {program.clubTypes.map(t => (
              <button key={t} onClick={() => change('type', t)}
                style={{
                  padding: '10px 16px', borderRadius: '99px', cursor: 'pointer',
                  border: `1px solid ${form.type === t ? program.color : T.line}`,
                  background: form.type === t ? program.color : '#fff',
                  color: form.type === t ? '#fff' : T.muted,
                  fontSize: '12.5px', fontWeight: 700, fontFamily: 'inherit',
                }}>{CLUB_TYPES[t]?.label || t}</button>
            ))}
          </div>

          <label style={S.label}>Jina la club *</label>
          <input style={{ ...S.input, marginBottom: '16px' }} value={form.name}
            onChange={e => change('name', e.target.value)}
            placeholder={program.defaultClub || 'mf. Kaloleni Primary Chess Club'} />

          <label style={S.label}>{CLUB_TYPES[form.type]?.locationLabel || 'Mahali'}</label>
          <input style={{ ...S.input, marginBottom: '16px' }} value={form.location}
            onChange={e => change('location', e.target.value)} />

          <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
            <div style={{ flex: '1 1 140px' }}>
              <label style={S.label}>Mkoa</label>
              <input style={{ ...S.input, marginBottom: '16px' }} value={form.region}
                onChange={e => change('region', e.target.value)} />
            </div>
            <div style={{ flex: '1 1 140px' }}>
              <label style={S.label}>Wilaya</label>
              <input style={{ ...S.input, marginBottom: '16px' }} value={form.district}
                onChange={e => change('district', e.target.value)} />
            </div>
          </div>

          <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
            <div style={{ flex: '1 1 140px' }}>
              <label style={S.label}>Siku ya kukutana</label>
              <select style={{ ...S.input, marginBottom: '16px' }} value={form.meetingDay}
                onChange={e => change('meetingDay', e.target.value)}>
                <option value="">— Chagua —</option>
                {DAYS.map(d => <option key={d} value={d}>{d}</option>)}
              </select>
            </div>
            <div style={{ flex: '1 1 110px' }}>
              <label style={S.label}>Muda</label>
              <input type="time" style={{ ...S.input, marginBottom: '16px' }} value={form.meetingTime}
                onChange={e => change('meetingTime', e.target.value)} />
            </div>
          </div>

          <label style={S.label}>Makocha wanaohusika</label>
          <input style={{ ...S.input, marginBottom: '16px' }} value={form.coachNames}
            onChange={e => change('coachNames', e.target.value)} placeholder="Majina, yakitenganishwa kwa koma" />

          <label style={S.label}>Maelezo</label>
          <textarea style={{ ...S.input, minHeight: '70px', marginBottom: '18px', resize: 'vertical' }}
            value={form.notes} onChange={e => change('notes', e.target.value)} />

          <div style={{ display: 'flex', gap: '10px' }}>
            <button style={{ ...S.btn, background: program.color, flex: 1, opacity: saving ? 0.6 : 1 }}
              disabled={saving} onClick={submit}>
              {saving ? 'Inahifadhi...' : editingId ? 'Hifadhi' : 'Tengeneza club'}
            </button>
            <button style={S.btnGhost} onClick={reset}>Ghairi</button>
          </div>
        </div>
      )}

      {clubs.length === 0 && !showForm ? (
        <div style={{ ...S.card, ...S.empty }}>
          Bado hakuna club kwenye programu hii. Bofya "+ Club" kutengeneza ya kwanza.
        </div>
      ) : clubs.map(c => (
        <div key={c.id} style={{ ...S.card, borderLeft: `4px solid ${program.color}` }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', gap: '10px' }}>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '9px' }}>
                <Icon name="chess" size={16} color={program.color} />
                <div style={{ fontSize: '15px', fontWeight: 700, color: T.ink }}>{c.name}</div>
              </div>
              <div style={{ fontSize: '11.5px', color: T.muted, marginTop: '7px', lineHeight: 1.7 }}>
                {CLUB_TYPES[c.type]?.label || c.type}
                {c.location ? ` · ${c.location}` : ''}
                {c.district ? ` · ${c.district}` : ''}
                <br />
                {c.meetingDay ? `${c.meetingDay} ${c.meetingTime || ''}` : 'Siku haijawekwa'}
                {c.coachNames ? ` · Makocha: ${c.coachNames}` : ''}
              </div>
              <div style={{ marginTop: '10px' }}>
                <span style={{ ...S.pill, color: program.color, background: `${program.color}15` }}>
                  {counts[c.id] || 0} wanachama
                </span>
              </div>
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
              <button style={{ ...S.btnGhost, padding: '6px 12px', fontSize: '11.5px' }}
                onClick={() => navigate(`/programs/${pid}/members`)}>Wanachama</button>
              <button style={{ ...S.btnGhost, padding: '6px 12px', fontSize: '11.5px' }}
                onClick={() => edit(c)}>Badilisha</button>
              {userRole === 'admin' && (
                <button style={{ ...S.btnGhost, padding: '6px 12px', fontSize: '11.5px', color: '#DC2626' }}
                  onClick={() => remove(c.id)}>Futa</button>
              )}
            </div>
          </div>
        </div>
      ))}

    </ProgramShell>
  );
}
